// achievements_ui.jsx — badges on Progress and the "unlocked" card on the summary.
//
//   AchievementsCard()                 Progress section body: recent badges, next up, all badges
//   AchievementsUnlocked({ session })  summary screen: badges first earned in this workout (null if none)
//   BadgeMedal({ a, size })            the medallion
//
// All data comes from achievements.js (deterministic, derived from history).

import React from 'react';
import { LLSheet } from './sheets.jsx';
import { useApp } from './store.jsx';
import { LL, LLIcon } from './tokens.jsx';
import { computeAchievements, achievementsEarnedIn, nextAchievements, ACH_CATEGORIES } from './achievements.js';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dateStr = (ms) => { const d = new Date(ms); return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`; };
const kicker = { fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 700, letterSpacing: 1.1, color: LL.fgDim, textTransform: 'uppercase' };
const reduced = () => { try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; } };

// Tier metals: bronze, silver, gold, platinum (hue-matched, legible in both themes).
const TIER = {
  1: { c: 'oklch(0.70 0.10 55)', name: 'Bronze' },
  2: { c: 'oklch(0.78 0.02 250)', name: 'Silver' },
  3: { c: 'oklch(0.82 0.15 85)', name: 'Gold' },
  4: { c: 'oklch(0.80 0.08 195)', name: 'Platinum' },
};

// 20×20 glyphs, stroke = currentColor.
const S = { stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round', fill: 'none' };
const GLYPH = {
  medal: <><circle cx="10" cy="12" r="4.5" {...S} /><path d="M7 3.5l2 4M13 3.5l-2 4M10 10v4" {...S} /></>,
  flame: <path d="M10 2.5c1 3 4 4 4 8a4 4 0 01-8 0c0-1.5.5-2.5 1.5-3.5 0 2 1 3 2 3 0-2-1-4 .5-7.5z" {...S} />,
  calendar: <><rect x="3.5" y="4.5" width="13" height="12" rx="2" {...S} /><path d="M3.5 8.5h13M7 3v3M13 3v3M7 12h2" {...S} /></>,
  return: <path d="M7 6.5L4 9.5l3 3M4.5 9.5H12a4 4 0 010 8h-1.5" {...S} />,
  sun: <><circle cx="10" cy="10" r="3.2" {...S} /><path d="M10 2.5v2M10 15.5v2M2.5 10h2M15.5 10h2M4.7 4.7l1.4 1.4M13.9 13.9l1.4 1.4M4.7 15.3l1.4-1.4M13.9 6.1l1.4-1.4" {...S} /></>,
  flag: <path d="M5 17.5V3.5M5 4h9l-2 3.5 2 3.5H5" {...S} />,
  stack: <path d="M10 3l7 3.5-7 3.5-7-3.5L10 3zM3 10l7 3.5 7-3.5M3 13.5L10 17l7-3.5" {...S} />,
  bolt: <path d="M11 2.5L4.5 11h5l-1 6.5L15.5 9h-5l.5-6.5z" {...S} />,
  trophy: <path d="M6 3.5h8v4a4 4 0 01-8 0v-4zM6 5H3.5v1.5A2.5 2.5 0 006 9M14 5h2.5v1.5A2.5 2.5 0 0114 9M10 11.5V14M7 16.5h6M8 14h4v2.5H8z" {...S} />,
  grid: <><rect x="3.5" y="3.5" width="5" height="5" rx="1" {...S} /><rect x="11.5" y="3.5" width="5" height="5" rx="1" {...S} /><rect x="3.5" y="11.5" width="5" height="5" rx="1" {...S} /><rect x="11.5" y="11.5" width="5" height="5" rx="1" {...S} /></>,
  gauge: <><path d="M3.5 14a6.5 6.5 0 1113 0" {...S} /><path d="M10 14l3-4.5" {...S} /><circle cx="10" cy="14" r="1" fill="currentColor" /></>,
  body: <><circle cx="10" cy="4" r="1.8" {...S} /><path d="M5 7.5h10M10 7.5v5M7.5 17.5l2.5-5 2.5 5" {...S} /></>,
};

export function BadgeMedal({ a, size = 56, earned = a.earned }) {
  const t = TIER[a.tier] || TIER[1];
  const inner = size - 8;
  const fill = earned ? `color-mix(in oklab, ${t.c} 22%, ${LL.surface})` : LL.surface2;
  const ring = earned ? t.c : LL.line2;
  const glyph = earned ? `color-mix(in oklab, ${t.c} 70%, ${LL.fg})` : LL.fgFaint;
  return (
    <div aria-hidden="true" style={{ width: size, height: size, position: 'relative', flexShrink: 0 }}>
      <svg width={size} height={size} viewBox="0 0 56 56" style={{ position: 'absolute', inset: 0 }}>
        {/* scalloped rim */}
        <path d={rosette(28, 28, 27, 24.5, 12)} style={{ fill: earned ? `color-mix(in oklab, ${t.c} 55%, ${LL.surface})` : LL.surface3, transition: `fill 240ms ${LL.ease}` }} />
        <circle cx="28" cy="28" r="21" style={{ fill, stroke: ring, strokeWidth: 1.5 }} />
      </svg>
      <div style={{ position: 'absolute', inset: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', color: glyph }}>
        {a.icon === 'plate' ? (
          <span style={{ fontFamily: LL.display, fontWeight: 800, fontSize: Math.round(inner * 0.42) /* token-ok: scales with the badge */, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{a.plates}</span>
        ) : (
          <svg width={Math.round(inner * 0.5)} height={Math.round(inner * 0.5)} viewBox="0 0 20 20">{GLYPH[a.icon] || GLYPH.medal}</svg>
        )}
      </div>
      {!earned && (
        <span style={{ position: 'absolute', right: -1, bottom: -1, width: Math.max(16, size * 0.32), height: Math.max(16, size * 0.32), borderRadius: LL.r.pill, background: LL.surface, border: `1px solid ${LL.line}`, color: LL.fgDim, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <LLIcon.lock style={{ width: Math.max(10, size * 0.18), height: Math.max(10, size * 0.18) }} />
        </span>
      )}
    </div>
  );
}

function rosette(cx, cy, ro, ri, n) {
  let d = '';
  for (let i = 0; i < n * 2; i++) {
    const r = i % 2 ? ri : ro;
    const ang = (Math.PI * i) / n - Math.PI / 2;
    d += `${i ? 'L' : 'M'}${(cx + r * Math.cos(ang)).toFixed(2)},${(cy + r * Math.sin(ang)).toFixed(2)}`;
  }
  return d + 'Z';
}

function Progress({ value, color = LL.accent }) {
  return (
    <div style={{ height: 6, borderRadius: LL.r.xs, background: LL.surface3, overflow: 'hidden' }} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(value * 100)}>
      <div style={{ width: `${Math.max(2, value * 100)}%`, height: '100%', background: color, borderRadius: LL.r.xs, transition: `width 600ms ${LL.ease}` }} />
    </div>
  );
}

export function useAchievements() {
  const { state } = useApp();
  return React.useMemo(() => computeAchievements(state), [state.sessions, state.exercises, state.programs, state.prefs.units]); // eslint-disable-line react-hooks/exhaustive-deps
}

export function AchievementsCard({ result }) {
  const { setRoute } = useApp();
  const own = useAchievements();
  const res = result || own;
  const [all, setAll] = React.useState(false);
  const [detail, setDetail] = React.useState(null);
  const recent = res.list.filter(a => a.earned).sort((a, b) => b.earnedAt - a.earnedAt || b.tier - a.tier).slice(0, 4);
  const next = nextAchievements(res, 3);

  return (
    <div style={{ background: LL.surface, border: `1px solid ${LL.line}`, borderRadius: LL.r.md, padding: 16 }}>
      {recent.length > 0 ? (
        <>
          <div style={kicker}>Latest</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginTop: 10 }}>
            {recent.map(a => (
              <button key={a.id} onClick={() => setDetail(a)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, minWidth: 0 }}>
                <BadgeMedal a={a} size={52} />
                <span style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 600, color: LL.fg, textAlign: 'center', lineHeight: 1.25, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{a.title}</span>
              </button>
            ))}
          </div>
        </>
      ) : (
        <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute, lineHeight: 1.45 }}>Badges unlock from your real training: workouts, streaks, plates on the bar and records.</div>
      )}

      {next.length > 0 && (
        <div style={{ marginTop: recent.length ? 16 : 12, paddingTop: recent.length ? 14 : 0, borderTop: recent.length ? `1px solid ${LL.line}` : 'none' }}>
          <div style={kicker}>Next up</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 10 }}>
            {next.map(a => (
              <button key={a.id} onClick={() => setDetail(a)} style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}>
                <BadgeMedal a={a} size={40} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 600, color: LL.fg, flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.title}</span>
                    <span style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{a.fmt(a.value)} / {a.fmt(a.target)}</span>
                  </span>
                  <span style={{ display: 'block', marginTop: 6 }}><Progress value={a.progress} /></span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      <button onClick={() => setAll(v => !v)} aria-expanded={all} style={{
        marginTop: 16, width: '100%', height: 42, borderRadius: LL.r.md, border: `1px solid ${LL.line2}`, background: LL.surface2, color: LL.fg,
        fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
      }}>
        {all ? 'Hide badges' : `All ${res.total} badges`}
        <LLIcon.chevDown style={{ width: 16, height: 16, transform: all ? 'rotate(180deg)' : 'none', transition: `transform 200ms ${LL.ease}` }} />
      </button>

      {all && (
        <div style={{ marginTop: 6 }}>
          {ACH_CATEGORIES.map(cat => {
            const items = res.list.filter(a => a.cat === cat.id);
            if (!items.length) return null;
            const got = items.filter(a => a.earned).length;
            return (
              <div key={cat.id} style={{ marginTop: 16 }}>
                <div style={{ display: 'flex', alignItems: 'baseline' }}>
                  <div style={kicker}>{cat.label}</div>
                  <div style={{ marginLeft: 'auto', fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute, fontVariantNumeric: 'tabular-nums' }}>{got}/{items.length}</div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px 8px', marginTop: 10 }}>
                  {items.map(a => (
                    <button key={a.id} onClick={() => setDetail(a)} aria-label={`${a.title}, ${a.earned ? 'earned' : 'locked'}`} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, minWidth: 0 }}>
                      <BadgeMedal a={a} size={46} />
                      <span style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 600, color: a.earned ? LL.fg : LL.fgDim, textAlign: 'center', lineHeight: 1.25, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{a.title}</span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <LLSheet open={!!detail} onClose={() => setDetail(null)} title={detail?.title || ''} subtitle={detail ? `${TIER[detail.tier]?.name} · ${ACH_CATEGORIES.find(c => c.id === detail.cat)?.label}` : ''}>
        {detail && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '6px 0 4px' }}>
            <BadgeMedal a={detail} size={96} />
            <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, color: LL.fg, marginTop: 14, lineHeight: 1.45 }}>{detail.desc}</div>
            {detail.earned ? (
              <>
                <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.accentText, fontWeight: 600, marginTop: 8 }}>Earned {dateStr(detail.earnedAt)}</div>
                {detail.sessionId && (
                  <button onClick={() => { const id = detail.sessionId; setDetail(null); setRoute('workout-review', { sessionId: id }); }} style={{
                    marginTop: 16, width: '100%', height: 46, borderRadius: LL.r.md, border: `1px solid ${LL.line2}`, background: LL.surface2, color: LL.fg,
                    fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, cursor: 'pointer',
                  }}>See that workout</button>
                )}
              </>
            ) : (
              <div style={{ width: '100%', marginTop: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute, marginBottom: 8, fontVariantNumeric: 'tabular-nums' }}>
                  <span>Progress</span><span style={{ color: LL.fg, fontWeight: 600 }}>{detail.fmt(detail.value)} of {detail.fmt(detail.target)}</span>
                </div>
                <Progress value={detail.progress} />
              </div>
            )}
          </div>
        )}
      </LLSheet>
    </div>
  );
}

// Summary screen: what this workout unlocked.
export function AchievementsUnlocked({ session, style }) {
  const { state } = useApp();
  const list = React.useMemo(() => (session && session.completedAt ? achievementsEarnedIn(state, session.id) : []), [state.sessions, state.exercises, state.programs, state.prefs.units, session?.id, session?.completedAt]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!list.length) return null;
  const anim = reduced() ? undefined : `llachPop 420ms ${LL.spring} both`;
  return (
    <div style={{
      background: `linear-gradient(135deg, color-mix(in oklab, ${LL.pr} 12%, ${LL.surface}), ${LL.surface})`,
      border: `1px solid color-mix(in oklab, ${LL.pr} 35%, ${LL.line})`, borderRadius: LL.r.md, padding: 14, ...style,
    }}>
      <style>{'@keyframes llachPop{0%{opacity:0;transform:scale(.6)}70%{opacity:1;transform:scale(1.06)}100%{transform:scale(1)}}'}</style>
      <div style={{ ...kicker, color: LL.prText }}>{list.length === 1 ? 'Achievement unlocked' : `${list.length} achievements unlocked`}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
        {list.map((a, i) => (
          <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ animation: anim, animationDelay: anim ? `${120 + i * 90}ms` : undefined }}><BadgeMedal a={a} size={48} /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 700, color: LL.fg }}>{a.title}</div>
              <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute, marginTop: 2, lineHeight: 1.4 }}>{a.desc}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
